import { Button } from '@growup/ui';
import type { FinanceDocument } from '@growup/site-shared';
import { useEffect, useRef, useState } from 'react';

import { fetchBlob, messageOf } from '../api';

/**
 * Miniatura e ecra inteiro de uma fatura (F-015/F-016). Os documentos sao
 * privados: vem com a sessao de quem gere o orcamento e ficam so em memoria
 * (blob:), nunca num link publico.
 */
export function InvoiceThumbnail({
  path,
  document: doc,
  onError,
}: {
  path: string;
  document: FinanceDocument;
  onError: (message: string) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const isImage = doc.contentType !== 'application/pdf';
  const name = doc.originalName || 'documento';

  useEffect(() => {
    let alive = true;
    let created: string | null = null;
    fetchBlob(path, doc.contentType)
      .then((blob) => {
        if (!alive) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch((e: unknown) => alive && onError(messageOf(e)));
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [path, doc.contentType, onError]);

  return (
    <>
      <button
        type="button"
        className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden border-2 border-gu-line bg-gu-sunken text-caption font-bold"
        aria-label={`Ver ${name} em ecrã inteiro`}
        disabled={url === null}
        onClick={() => setOpen(true)}
      >
        {isImage && url ? (
          <img src={url} alt="" className="h-full w-full object-cover" />
        ) : (
          <span aria-hidden="true">{isImage ? '…' : 'PDF'}</span>
        )}
      </button>
      {open && url ? (
        <InvoiceViewer url={url} name={name} isImage={isImage} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

function InvoiceViewer({
  url,
  name,
  isImage,
  onClose,
}: {
  url: string;
  name: string;
  isImage: boolean;
  onClose: () => void;
}) {
  const [zoomed, setZoomed] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={name}
      className="fixed inset-0 z-50 flex flex-col bg-black/90 text-white"
    >
      <div className="flex flex-wrap items-center gap-2 p-3">
        <span className="min-w-0 flex-1 truncate font-semibold">{name}</span>
        {isImage ? (
          <Button size="sm" variant="secondary" onClick={() => setZoomed((z) => !z)}>
            {zoomed ? 'Ajustar ao ecrã' : 'Tamanho real'}
          </Button>
        ) : null}
        <a
          href={url}
          download={name}
          className="px-3 py-1.5 text-label font-bold underline underline-offset-4"
        >
          Descarregar
        </a>
        <button
          ref={closeButton}
          type="button"
          className="px-3 py-1.5 text-label font-bold"
          onClick={onClose}
        >
          Fechar ✕
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {isImage ? (
          <img
            src={url}
            alt={name}
            className={
              zoomed
                ? 'max-w-none cursor-zoom-out'
                : 'mx-auto h-full w-full cursor-zoom-in object-contain'
            }
            onClick={() => setZoomed((z) => !z)}
          />
        ) : (
          <iframe src={url} title={name} className="h-full w-full bg-white" />
        )}
      </div>
    </div>
  );
}
